/**
 * Public open challenge queue — browse, post, accept without pre-existing friendship.
 */
import {
  collection,
  doc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { CHALLENGES_COLLECTION } from './challengeService';
import { inferOpponentSide } from '../utils/customPropSides';

export const OPEN_CHALLENGES_COLLECTION = 'open_challenges';

export const OpenChallengeStatus = {
  OPEN: 'open',
  MATCHED: 'matched',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
};

const OPEN_CHALLENGE_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_OPEN_QUEUE_LIST = 50;
const MAX_RECENT_MATCHED = 20;
const CLOSING_SOON_MS = 60 * 60 * 1000;

function mapOpenDoc(id, data) {
  const createdAt = data.createdAt?.toMillis?.() ?? data.createdAt ?? null;
  const expiresAt = data.expiresAt?.toMillis?.() ?? data.expiresAt ?? null;
  const acceptedAt = data.acceptedAt?.toMillis?.() ?? data.acceptedAt ?? null;
  return {
    id,
    creatorId: data.creatorId ?? '',
    creatorUsername: data.creatorUsername ?? 'Player',
    wager: data.wager ?? '',
    creatorSide: data.creatorSide ?? '',
    opponentSide: data.opponentSide ?? '',
    stake: Number(data.stake) || 0,
    sport: data.sport ?? null,
    gameId: data.gameId ?? null,
    status: data.status ?? OpenChallengeStatus.OPEN,
    createdAt,
    expiresAt,
    acceptedBy: data.acceptedBy ?? null,
    acceptedAt,
    expiredAt: data.expiredAt?.toMillis?.() ?? data.expiredAt ?? null,
    expireReason: data.expireReason ?? null,
    groupId: data.groupId ?? null,
    groupName: data.groupName ?? null,
  };
}

/** Human-readable time until expiresAt (or null if none). */
export function formatTimeRemaining(expiresAtMs) {
  if (!expiresAtMs) return null;
  const diff = expiresAtMs - Date.now();
  if (diff <= 0) return 'Expired';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/** UI status label + tone for open queue cards. */
export function getOpenLockDisplayStatus(challenge) {
  const now = Date.now();
  if (challenge.status === OpenChallengeStatus.EXPIRED) {
    return { label: 'Expired', tone: 'neutral' };
  }
  if (challenge.status === OpenChallengeStatus.MATCHED) {
    return { label: 'Matched', tone: 'brand' };
  }
  if (challenge.expiresAt && challenge.expiresAt <= now) {
    return { label: 'Expired', tone: 'neutral' };
  }
  if (challenge.expiresAt && challenge.expiresAt - now <= CLOSING_SOON_MS) {
    return { label: 'Closing Soon', tone: 'warning' };
  }
  return { label: 'Open', tone: 'brand' };
}

export function resolveOpenLockGameLabel(challenge, gameById = {}) {
  if (!challenge?.gameId) {
    return challenge?.sport ? { sport: challenge.sport, matchup: null } : null;
  }
  const game = gameById[challenge.gameId];
  if (!game) {
    return { sport: challenge.sport || 'Game', matchup: null };
  }
  const a = game.a || game.homeTeam || '';
  const b = game.b || game.awayTeam || '';
  return {
    sport: game.sport || challenge.sport || '',
    matchup: a && b ? `${a} vs ${b}` : null,
    startTime: game.startTime || null,
    status: game.status || null,
  };
}

/** Real-time public open queue (excludes group-scoped locks). */
export function subscribeOpenChallenges(onList, onError) {
  const col = collection(db, OPEN_CHALLENGES_COLLECTION);
  const q = query(
    col,
    where('groupId', '==', null),
    where('status', '==', OpenChallengeStatus.OPEN),
    orderBy('createdAt', 'desc'),
    limit(MAX_OPEN_QUEUE_LIST)
  );

  return onSnapshot(
    q,
    (snap) => {
      const now = Date.now();
      const list = snap.docs
        .map((d) => mapOpenDoc(d.id, d.data()))
        .filter((c) => !c.expiresAt || c.expiresAt > now);
      onList(list);
    },
    (err) => {
      console.error('subscribeOpenChallenges:', err);
      onError?.(err);
      onList([]);
    }
  );
}

/** Recently matched public open locks (social proof). */
export function subscribeRecentlyMatchedOpenChallenges(onList, onError) {
  const col = collection(db, OPEN_CHALLENGES_COLLECTION);
  const q = query(
    col,
    where('groupId', '==', null),
    where('status', '==', OpenChallengeStatus.MATCHED),
    orderBy('acceptedAt', 'desc'),
    limit(MAX_RECENT_MATCHED)
  );

  return onSnapshot(
    q,
    (snap) => {
      onList(snap.docs.map((d) => mapOpenDoc(d.id, d.data())));
    },
    (err) => {
      console.error('subscribeRecentlyMatchedOpenChallenges:', err);
      onError?.(err);
      onList([]);
    }
  );
}

/** Creator's waiting public open challenges. */
export function subscribeMyOpenChallenges(creatorId, onList, onError) {
  if (!creatorId) {
    onList([]);
    return () => {};
  }
  const col = collection(db, OPEN_CHALLENGES_COLLECTION);
  const q = query(
    col,
    where('creatorId', '==', creatorId),
    where('groupId', '==', null),
    where('status', '==', OpenChallengeStatus.OPEN),
    orderBy('createdAt', 'desc'),
    limit(20)
  );

  return onSnapshot(
    q,
    (snap) => {
      const now = Date.now();
      onList(
        snap.docs
          .map((d) => mapOpenDoc(d.id, d.data()))
          .filter((c) => !c.expiresAt || c.expiresAt > now)
      );
    },
    (err) => {
      console.error('subscribeMyOpenChallenges:', err);
      onError?.(err);
      onList([]);
    }
  );
}

/** Creator's terminal public open locks (matched / expired / canceled) for Completed. */
export function subscribeMyTerminalOpenChallenges(creatorId, onList, onError) {
  if (!creatorId) {
    onList([]);
    return () => {};
  }
  const col = collection(db, OPEN_CHALLENGES_COLLECTION);
  const q = query(
    col,
    where('creatorId', '==', creatorId),
    where('groupId', '==', null),
    where('status', 'in', [
      OpenChallengeStatus.MATCHED,
      OpenChallengeStatus.EXPIRED,
      OpenChallengeStatus.CANCELLED,
    ]),
    orderBy('createdAt', 'desc'),
    limit(30),
  );

  return onSnapshot(
    q,
    (snap) => {
      onList(snap.docs.map((d) => mapOpenDoc(d.id, d.data())));
    },
    (err) => {
      console.error('subscribeMyTerminalOpenChallenges:', err);
      onError?.(err);
      onList([]);
    },
  );
}

/**
 * @param {object} params
 * @returns {Promise<string>} document id (use for wallet matchId before write)
 */
export function buildOpenChallengeRef() {
  return doc(collection(db, OPEN_CHALLENGES_COLLECTION));
}

export async function writeOpenChallenge(ref, payload) {
  const expiresAt = Timestamp.fromMillis(Date.now() + OPEN_CHALLENGE_TTL_MS);
  const groupId = payload.groupId?.trim?.() || payload.groupId || null;
  await setDoc(ref, {
    creatorId: payload.creatorId,
    creatorUsername: payload.creatorUsername,
    wager: payload.wager,
    creatorSide: payload.creatorSide,
    opponentSide: payload.opponentSide,
    stake: payload.stake,
    sport: payload.sport ?? null,
    gameId: payload.gameId ?? null,
    groupId: groupId || null,
    groupName: groupId ? (payload.groupName || null) : null,
    status: OpenChallengeStatus.OPEN,
    createdAt: serverTimestamp(),
    expiresAt,
    acceptedBy: null,
    acceptedAt: null,
  });
}

/** Group-scoped open locks for a league roster. */
export function subscribeGroupOpenChallenges(groupId, onList, onError) {
  if (!groupId) {
    onList([]);
    return () => {};
  }
  const col = collection(db, OPEN_CHALLENGES_COLLECTION);
  const q = query(
    col,
    where('groupId', '==', groupId),
    where('status', '==', OpenChallengeStatus.OPEN),
    orderBy('createdAt', 'desc'),
    limit(MAX_OPEN_QUEUE_LIST)
  );
  return onSnapshot(
    q,
    (snap) => {
      const now = Date.now();
      onList(
        snap.docs
          .map((d) => mapOpenDoc(d.id, d.data()))
          .filter((c) => !c.expiresAt || c.expiresAt > now)
      );
    },
    (err) => {
      console.error('subscribeGroupOpenChallenges:', err);
      onError?.(err);
      onList([]);
    }
  );
}

/** Creator's open locks within a group. */
export function subscribeMyGroupOpenChallenges(creatorId, groupId, onList, onError) {
  if (!creatorId || !groupId) {
    onList([]);
    return () => {};
  }
  const col = collection(db, OPEN_CHALLENGES_COLLECTION);
  const q = query(
    col,
    where('creatorId', '==', creatorId),
    where('groupId', '==', groupId),
    where('status', '==', OpenChallengeStatus.OPEN),
    orderBy('createdAt', 'desc'),
    limit(20)
  );
  return onSnapshot(
    q,
    (snap) => {
      const now = Date.now();
      onList(
        snap.docs
          .map((d) => mapOpenDoc(d.id, d.data()))
          .filter((c) => !c.expiresAt || c.expiresAt > now)
      );
    },
    (err) => {
      console.error('subscribeMyGroupOpenChallenges:', err);
      onError?.(err);
      onList([]);
    }
  );
}

/**
 * Atomically match an open challenge and promote to challenges collection.
 * @returns {Promise<string>} new challenge document id
 */
export async function acceptOpenChallengeTransaction({
  openChallengeId,
  acceptorId,
  acceptorName,
  acceptorRiskTally = 0,
  creatorRiskTally = 0,
  challengeRef: presetChallengeRef,
}) {
  const openRef = doc(db, OPEN_CHALLENGES_COLLECTION, openChallengeId);

  return runTransaction(db, async (transaction) => {
    const openSnap = await transaction.get(openRef);
    if (!openSnap.exists()) throw new Error('CHALLENGE_NOT_FOUND');

    const open = openSnap.data();
    if (open.status !== OpenChallengeStatus.OPEN) throw new Error('CHALLENGE_NOT_OPEN');
    if (open.creatorId === acceptorId) throw new Error('CANNOT_ACCEPT_OWN');

    const expiresAtMs = open.expiresAt?.toMillis?.() ?? open.expiresAt;
    if (expiresAtMs && expiresAtMs <= Date.now()) throw new Error('CHALLENGE_EXPIRED');

    const stake = Number(open.stake) || 0;
    const creatorId = open.creatorId;
    const creatorSide = open.creatorSide || '';
    const opponentSide = open.opponentSide || inferOpponentSide(creatorSide);
    const participantIds = [creatorId, acceptorId].sort();

    const challengeRef = presetChallengeRef || doc(collection(db, CHALLENGES_COLLECTION));

    transaction.update(openRef, {
      status: OpenChallengeStatus.MATCHED,
      acceptedBy: acceptorId,
      acceptedAt: serverTimestamp(),
    });

    transaction.set(challengeRef, {
      creatorId,
      recipientId: acceptorId,
      participantIds,
      status: 'LOCKED_IN',
      totalPot: stake * 2,
      creatorRiskTally,
      recipientRiskTally: acceptorRiskTally,
      agreedBy: [creatorId, acceptorId],
      participants: [
        {
          id: creatorId,
          name: open.creatorUsername || 'Player',
          amount: stake,
          pick: creatorSide,
          riskTally: creatorRiskTally,
        },
        {
          id: acceptorId,
          name: acceptorName || 'Player',
          amount: stake,
          pick: opponentSide,
          riskTally: acceptorRiskTally,
        },
      ],
      matchup: {
        isCustom: true,
        description: open.wager || '',
        gameStatus: 'PENDING',
        creatorPick: creatorSide,
        creatorPickLocked: true,
        opponentPick: opponentSide,
        sport: open.sport ?? null,
        gameId: open.gameId ?? null,
      },
      challengeType: 'custom',
      promotedFromOpenQueueId: openChallengeId,
      recipientNotified: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return challengeRef.id;
  });
}

export async function cancelOpenChallenge(openChallengeId, creatorId) {
  const openRef = doc(db, OPEN_CHALLENGES_COLLECTION, openChallengeId);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(openRef);
    if (!snap.exists()) throw new Error('CHALLENGE_NOT_FOUND');
    const data = snap.data();
    if (data.creatorId !== creatorId) throw new Error('NOT_CREATOR');
    if (data.status !== OpenChallengeStatus.OPEN) throw new Error('CHALLENGE_NOT_OPEN');
    transaction.update(openRef, {
      status: OpenChallengeStatus.CANCELLED,
      updatedAt: serverTimestamp(),
    });
  });
}

/**
 * Undo a failed accept — reopen queue entry and remove orphan challenge doc.
 * Used when acceptor wallet deduct fails after the match transaction commits.
 */
export async function revertOpenChallengeAccept({
  openChallengeId,
  challengeId,
  actorId,
}) {
  const openRef = doc(db, OPEN_CHALLENGES_COLLECTION, openChallengeId);
  const challengeRef = doc(db, CHALLENGES_COLLECTION, challengeId);

  await runTransaction(db, async (transaction) => {
    const openSnap = await transaction.get(openRef);
    if (!openSnap.exists()) throw new Error('CHALLENGE_NOT_FOUND');

    const open = openSnap.data();
    const canRevert =
      actorId === open.creatorId || actorId === open.acceptedBy;
    if (!canRevert) throw new Error('NOT_AUTHORIZED');

    if (open.status === OpenChallengeStatus.MATCHED) {
      transaction.update(openRef, {
        status: OpenChallengeStatus.OPEN,
        acceptedBy: null,
        acceptedAt: null,
        updatedAt: serverTimestamp(),
      });
    }

    const challengeSnap = await transaction.get(challengeRef);
    if (challengeSnap.exists()) {
      transaction.delete(challengeRef);
    }
  });
}
